// DEPLOY: /var/www/woody.wtf/frontend/src/chart/PerformanceEngine.js
// Multi-index percentage chart. Shares Chart2's Canvas/theme approach, not its candle model.
import { readTheme } from './theme';

const DAY = 86400000;
const LEFT = 54, RIGHT = 12, TOP = 14, BOTTOM = 30;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

export function periodStart(end, period) {
  const d = new Date(end);
  if (period === '1W') d.setUTCDate(d.getUTCDate() - 7);
  else if (period.endsWith('M')) d.setUTCMonth(d.getUTCMonth() - Number(period.slice(0, -1)));
  else d.setUTCFullYear(d.getUTCFullYear() - Number(period.slice(0, -1)));
  return d.getTime();
}

export function normaliseIndexes(series, start, end) {
  return series.map(s => {
    const points = s.points.filter(([t, c]) => t >= start && t <= end && Number.isFinite(c) && c > 0);
    const base = points[0]?.[1];
    const line = base ? points.map(([t, c]) => [t, (c / base - 1) * 100]) : [];
    return { ...s, line, pct: line.length ? line[line.length - 1][1] : null,
             lastDate: line.length ? line[line.length - 1][0] : null };
  });
}

function niceStep(v) {
  const p = 10 ** Math.floor(Math.log10(v || 1));
  const f = v / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
}

export default class PerformanceEngine {
  constructor(host, onRank) {
    this.host = host;
    this.onRank = onRank;
    this.theme = readTheme(host);
    this.series = [];
    this.focused = null;
    this.cursor = null;
    this.pointers = new Map();
    this.period = '1Y';
    this.min = this.max = 0;
    this.start = this.end = 0;
    this.frame = 0;

    this.base = document.createElement('canvas');
    this.over = document.createElement('canvas');
    for (const canvas of [this.base, this.over]) {
      Object.assign(canvas.style, { position: 'absolute', inset: 0, width: '100%', height: '100%' });
      host.appendChild(canvas);
    }
    this.over.style.touchAction = 'none';
    this.down = e => this.pointerDown(e);
    this.move = e => this.pointerMove(e);
    this.up = e => this.pointerUp(e);
    this.wheel = e => this.onWheel(e);
    this.double = () => this.setPeriod(this.period);
    this.leave = () => { if (!this.pointers.size) { this.cursor = null; this.drawOver(); } };
    for (const [event, fn] of [['pointerdown', this.down], ['pointermove', this.move],
                              ['pointerup', this.up], ['pointercancel', this.up],
                              ['pointerleave', this.leave], ['wheel', this.wheel],
                              ['dblclick', this.double]]) {
      this.over.addEventListener(event, fn, event === 'wheel' ? { passive: false } : undefined);
    }
    this.observer = new ResizeObserver(() => this.resize());
    this.observer.observe(host);
    this.resize();
  }

  destroy() {
    this.observer.disconnect();
    cancelAnimationFrame(this.frame);
    for (const [event, fn] of [['pointerdown', this.down], ['pointermove', this.move],
                              ['pointerup', this.up], ['pointercancel', this.up],
                              ['pointerleave', this.leave], ['wheel', this.wheel],
                              ['dblclick', this.double]]) this.over.removeEventListener(event, fn);
    this.base.remove();
    this.over.remove();
  }

  get plotW() { return Math.max(1, this.w - LEFT - RIGHT); }
  get plotH() { return Math.max(1, this.h - TOP - BOTTOM); }
  x(t) { return LEFT + (t - this.start) / (this.end - this.start || 1) * this.plotW; }
  y(v) { return TOP + (this.hi - v) / (this.hi - this.lo || 1) * this.plotH; }
  pointer(e) {
    const r = this.over.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  setData(series) {
    this.series = series.filter(s => s.points.length);
    const dates = this.series.flatMap(s => [s.points[0][0], s.points[s.points.length - 1][0]]);
    this.min = Math.min(...dates);
    this.max = Math.max(...dates);
    if (dates.length) this.setPeriod(this.period);
    else { this.lines = []; this.ranking = []; this.onRank([]); this.schedule(); }
  }

  setPeriod(period) {
    this.period = period;
    if (this.max > this.min) this.setWindow(periodStart(this.max, period), this.max);
  }

  setWindow(start, end) {
    const span = clamp(end - start, 2 * DAY, this.max - this.min);
    this.start = clamp(start, this.min, this.max - span);
    this.end = this.start + span;
    this.lines = normaliseIndexes(this.series, this.start, this.end);
    this.ranking = [...this.lines].sort((a, b) => (b.pct ?? -Infinity) - (a.pct ?? -Infinity) || a.key.localeCompare(b.key));
    this.onRank(this.ranking);
    this.schedule();
  }

  focus(key) {
    if (this.focused === key) return;
    this.focused = key;
    this.schedule();
  }

  setAppearance(colors, hidden) {
    const style = s => ({ ...s, color: colors[s.key] || s.baseColor || s.color, hidden: !!hidden[s.key] });
    this.series = this.series.map(style);
    this.lines = (this.lines || []).map(style);
    this.ranking = [...this.lines].sort((a, b) => (b.pct ?? -Infinity) - (a.pct ?? -Infinity) || a.key.localeCompare(b.key));
    this.onRank(this.ranking);
    if (this.focused && this.lines.find(s => s.key === this.focused)?.hidden) this.focused = null;
    this.schedule();
  }

  resize() {
    const r = this.host.getBoundingClientRect();
    this.w = r.width;
    this.h = r.height;
    this.dpr = window.devicePixelRatio || 1;
    for (const c of [this.base, this.over]) {
      c.width = Math.round(this.w * this.dpr);
      c.height = Math.round(this.h * this.dpr);
    }
    this.schedule();
  }

  ctx(canvas) {
    const g = canvas.getContext('2d');
    g.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    g.clearRect(0, 0, this.w, this.h);
    return g;
  }

  schedule() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => { this.drawBase(); this.drawOver(); });
  }

  drawPath(g, line) {
    if (!line.length) return;
    g.beginPath();
    line.forEach(([t, pct], i) => {
      const x = this.x(t), y = this.y(pct);
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    });
    if (line.length === 1) { g.arc(this.x(line[0][0]), this.y(line[0][1]), 2, 0, Math.PI * 2); g.fill(); }
    else g.stroke();
  }

  drawBase() {
    if (!this.w || !this.h) return;
    const g = this.ctx(this.base), T = this.theme;
    g.fillStyle = T.bg;
    g.fillRect(0, 0, this.w, this.h);
    if (!this.lines?.length) return;

    let low = 0, high = 0;
    for (const s of this.lines) if (!s.hidden) for (const [, value] of s.line) {
      if (value < low) low = value;
      if (value > high) high = value;
    }
    const padding = Math.max(0.15, (high - low) * 0.07);
    this.lo = low - padding;
    this.hi = high + padding;
    const step = niceStep((this.hi - this.lo) / Math.max(2, Math.floor(this.plotH / 52)));
    g.font = T.font;
    g.textBaseline = 'middle';
    for (let tick = Math.ceil(this.lo / step) * step; tick <= this.hi; tick += step) {
      const y = this.y(tick);
      g.strokeStyle = T.grid;
      g.lineWidth = tick === 0 ? 1.5 : 0.7;
      g.setLineDash(tick === 0 ? [4, 3] : []);
      g.beginPath(); g.moveTo(LEFT, y); g.lineTo(this.w - RIGHT, y); g.stroke();
      g.setLineDash([]);
      g.fillStyle = T.muted;
      g.textAlign = 'right';
      g.fillText((tick > 0 ? '+' : '') + (Math.abs(tick) < 10 ? tick.toFixed(1) : tick.toFixed(0)) + '%', LEFT - 6, y);
    }

    const ticks = Math.max(2, Math.floor(this.plotW / 105));
    g.textAlign = 'center'; g.textBaseline = 'alphabetic';
    for (let i = 0; i <= ticks; i++) {
      const t = this.start + i * (this.end - this.start) / ticks;
      const x = this.x(t), d = new Date(t);
      g.fillStyle = T.muted;
      g.fillText(d.toLocaleDateString('en-AU', { day: this.end - this.start <= 35 * DAY ? 'numeric' : undefined,
                                                month: 'short', year: '2-digit', timeZone: 'UTC' }),
                 clamp(x, LEFT + 24, this.w - RIGHT - 24), this.h - 8);
    }

    g.save();
    g.beginPath();
    g.rect(LEFT, TOP, this.plotW, this.plotH);
    g.clip();
    g.lineJoin = 'round';
    for (const s of this.lines) {
      if (!s.line.length || s.hidden) continue;
      g.strokeStyle = s.color;
      g.fillStyle = s.color;
      g.globalAlpha = this.focused ? 0.12 : 0.72;
      g.lineWidth = 1.25;
      this.drawPath(g, s.line);
    }
    g.restore();
  }

  drawOver() {
    if (!this.w || !this.h) return;
    const g = this.ctx(this.over), T = this.theme;
    if (!this.lines?.length) return;
    if (this.focused) {
      const s = this.lines.find(row => row.key === this.focused);
      if (s?.line.length && !s.hidden) {
        g.save();
        g.beginPath(); g.rect(LEFT, TOP, this.plotW, this.plotH); g.clip();
        g.strokeStyle = g.fillStyle = s.color;
        g.lineWidth = 3;
        g.lineJoin = 'round';
        this.drawPath(g, s.line);
        g.restore();
      }
    }
    if (this.cursor != null && this.cursor >= LEFT && this.cursor <= this.w - RIGHT) {
      const t = this.start + (this.cursor - LEFT) / this.plotW * (this.end - this.start);
      g.strokeStyle = T.accent;
      g.setLineDash([3, 4]);
      g.beginPath(); g.moveTo(this.cursor, TOP); g.lineTo(this.cursor, this.h - BOTTOM); g.stroke();
      g.setLineDash([]);
      const label = new Date(t).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
      g.font = T.font;
      const width = g.measureText(label).width + 12;
      const x = clamp(this.cursor - width / 2, LEFT, this.w - RIGHT - width);
      g.fillStyle = T.accent; g.fillRect(x, this.h - BOTTOM, width, 19);
      g.fillStyle = '#fff'; g.textBaseline = 'middle'; g.fillText(label, x + 6, this.h - BOTTOM + 9);
    }
  }

  pointerDown(e) {
    this.over.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, this.pointer(e));
    if (this.pointers.size === 1) {
      this.drag = { x: this.pointer(e).x, start: this.start, end: this.end, moved: false };
      this.pinch = null;
    } else if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      const x = (a.x + b.x) / 2;
      this.pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y),
                     span: this.end - this.start,
                     anchor: this.start + (x - LEFT) / this.plotW * (this.end - this.start) };
    }
  }

  pointerMove(e) {
    const p = this.pointer(e);
    if (!this.pointers.has(e.pointerId)) {
      if (e.pointerType === 'mouse') { this.cursor = p.x; this.drawOver(); }
      return;
    }
    this.pointers.set(e.pointerId, p);
    if (this.pointers.size === 2 && this.pinch) {
      const [a, b] = [...this.pointers.values()];
      const x = (a.x + b.x) / 2;
      const distance = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
      const span = this.pinch.span * this.pinch.distance / distance;
      this.setWindow(this.pinch.anchor - (x - LEFT) / this.plotW * span,
                     this.pinch.anchor + (1 - (x - LEFT) / this.plotW) * span);
    } else if (this.pointers.size === 1 && this.drag) {
      const delta = p.x - this.drag.x;
      if (Math.abs(delta) > 3) this.drag.moved = true;
      if (this.drag.moved) {
        const offset = delta / this.plotW * (this.drag.end - this.drag.start);
        this.setWindow(this.drag.start - offset, this.drag.end - offset);
      }
    }
  }

  pointerUp(e) {
    const p = this.pointers.get(e.pointerId);
    this.pointers.delete(e.pointerId);
    if (p && this.pointers.size === 0) {
      if (!this.drag?.moved && !this.pinch) this.cursor = p.x;
      this.drag = this.pinch = null;
      this.drawOver();
    } else if (this.pointers.size === 1) {
      this.drag = null;
      this.pinch = null;
    }
  }

  onWheel(e) {
    if (this.max <= this.min) return;
    e.preventDefault();
    const p = this.pointer(e);
    const span = this.end - this.start;
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      const shift = e.deltaX / this.plotW * span;
      this.setWindow(this.start + shift, this.end + shift);
    } else {
      const scale = Math.exp(e.deltaY * (e.ctrlKey ? 0.01 : 0.002));
      const portion = clamp((p.x - LEFT) / this.plotW, 0, 1);
      const next = span * scale, anchor = this.start + portion * span;
      this.setWindow(anchor - portion * next, anchor + (1 - portion) * next);
    }
  }
}
